import { Button, CustomClaimValue, Facilities, Facility, FacilityLayoutConfiguration, Icons, Target, Targets } from "@app/shared";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toastError, toastSuccess } from "../../../helpers";
import { useLiveViewHub } from "../../../hooks/useLiveViewHub";
import { AuthorizeView } from "../AuthorizeView";
import { NumericInput } from "../inputs";
import {
    ATTENUATOR_ZONE_NAME,
    buildAttenuatorZonePoints,
    createDefaultAttenuatorZone,
    DEFAULT_LENGTH,
    DEFAULT_WIDTH,
    findAttenuatorZone,
    getDimensionsFromZone,
    MAX_LENGTH,
    MAX_WIDTH,
    MIN_DIM,
} from "./attenuatorLayoutHelpers";
import { FacilityBackgroundView } from "./FacilityBackgroundView";
import { zoneLengthOutOfRange, zoneWidthOutOfRange } from "@app/shared/src/helpers/sidePassGeometry";

type AttenuatorLayoutEditorProps = {
    facility: Facility;
    onChange: () => void;
};

export const AttenuatorLayoutEditor: React.FC<AttenuatorLayoutEditorProps> = ({ facility, onChange }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [width, setWidth] = useState(DEFAULT_WIDTH);
    const [length, setLength] = useState(DEFAULT_LENGTH);
    const autoCreatedRef = useRef(false);

    // Shown as stored rather than quietly clamped: a zone the editor rewrote on load would save a
    // footprint nobody chose. Out of range is surfaced and blocks the save until it is corrected.
    const widthOutOfRange = zoneWidthOutOfRange(width);
    const lengthOutOfRange = zoneLengthOutOfRange(length);
    const outOfRange = widthOutOfRange || lengthOutOfRange;

    // Load saved state from zone
    useEffect(() => {
        const zone = findAttenuatorZone(facility);
        if (zone) {
            const dims = getDimensionsFromZone(zone);
            setWidth(dims.width);
            setLength(dims.length);
        }
    }, [facility]);

    // auto create zone if none exists
    useEffect(() => {
        if (autoCreatedRef.current) return;
        if (findAttenuatorZone(facility)) return;
        if (!facility.id) return;

        autoCreatedRef.current = true;
        const zone = createDefaultAttenuatorZone(facility.id);

        const config = new FacilityLayoutConfiguration();
        // The layout endpoint soft-deletes any shape absent from the body, so every zone the truck
        // already has — its side-pass zones in particular — must ride along or this "create" would
        // quietly destroy them (and orphan the triggers watching them).
        config.detectionZones = [...(facility.detectionZones ?? []), zone];
        config.screenLines = facility.screenLines ?? [];
        config.laneLines = facility.laneLines ?? [];
        config.devices = facility.devices ?? [];
        config.referencePoint = facility.originReferencePointLatLng ?? undefined;

        Facilities.updateLayoutConfiguration(facility.id, config)
            .then(() => onChange())
            .catch((err) => console.error("Failed to auto-create attenuator zone:", err));
    }, [facility]); // eslint-disable-line react-hooks/exhaustive-deps

    const MAX_TARGETS = 15;
    const [recentTargets, setRecentTargets] = useState<Target[]>([]);
    const device = facility.devices?.[0];

    useEffect(() => {
        if (!device?.id) return;
        Targets.getRecentTargetPaths(device.id, String(MAX_TARGETS)).then((targets) => {
            if (targets) setRecentTargets(targets);
        });
    }, [device?.id]);

    const handleLiveTargets = useCallback((incoming: Target[]) => {
        setRecentTargets((prev) => {
            const updated = [...incoming, ...prev];
            return updated.slice(0, MAX_TARGETS);
        });
    }, []);
    useLiveViewHub(facility.id, handleLiveTargets);

    const saveZone = useCallback(
        async (w: number, l: number) => {
            const existingZone = findAttenuatorZone(facility);
            const zone = existingZone ?? createDefaultAttenuatorZone(facility.id!);
            zone.points = buildAttenuatorZonePoints(w, l);
            // Drops any legacy ":left"/":right" lane suffix the moment a layout is re-saved.
            zone.name = ATTENUATOR_ZONE_NAME;

            const config = new FacilityLayoutConfiguration();
            // Everything else the truck has rides along untouched: the layout endpoint soft-deletes
            // any shape absent from the body, and this editor owns only the cushion — sending the
            // cushion alone erased the side-pass zones every time someone pressed Save.
            const otherZones = (facility.detectionZones ?? []).filter((z) => z !== existingZone);
            config.detectionZones = [zone, ...otherZones];
            config.screenLines = facility.screenLines ?? [];
            config.laneLines = facility.laneLines ?? [];
            config.devices = facility.devices ?? [];
            config.referencePoint = facility.originReferencePointLatLng ?? undefined;

            await Facilities.updateLayoutConfiguration(facility.id, config);
        },
        [facility],
    );

    const handleSave = useCallback(async () => {
        // Guarded here as well as on the button: what this writes is what the truck runs.
        if (zoneWidthOutOfRange(width) || zoneLengthOutOfRange(length)) {
            toastError(`Cushion must be ${MIN_DIM}–${MAX_WIDTH} m wide and ${MIN_DIM}–${MAX_LENGTH} m long.`);
            return;
        }
        setIsSaving(true);
        try {
            await saveZone(width, length);
            toastSuccess("Layout saved successfully!");
            setIsEditing(false);
            onChange();
        } catch (error) {
            console.error("Failed to save layout:", error);
            toastError("Save failed", "Could not save the detection zone. Check the console for details.");
        } finally {
            setIsSaving(false);
        }
    }, [saveZone, width, length, onChange]);

    const handleCancel = useCallback(() => {
        const zone = findAttenuatorZone(facility);
        if (zone) {
            const dims = getDimensionsFromZone(zone);
            setWidth(dims.width);
            setLength(dims.length);
        } else {
            setWidth(DEFAULT_WIDTH);
            setLength(DEFAULT_LENGTH);
        }
        setIsEditing(false);
    }, [facility]);

    return (
        <div className="flex flex-col gap-4 p-4">
            <div className="flex flex-col xl:flex-row gap-4">
            {/* Road visual */}
            <div className="flex-1 h-[80vh] max-h-[900px] min-h-[600px] bg-white rounded-lg border border-gray-200 p-4 flex items-center justify-center overflow-auto">
                <FacilityBackgroundView
                    facility={facility}
                    targets={recentTargets}
                    attenuatorOverrides={{ width, length }}
                />
            </div>

            <div className="w-full xl:w-80 flex flex-col gap-4 items-start">
                <h3 className="text-lg font-semibold">Attenuator Truck Layout</h3>

                <p className="text-sm text-gray-500">
                    The truck is drawn centred, with traffic assumed on both sides. Which side a vehicle passed on is
                    reported per event rather than configured here.
                </p>

                <div className="w-full m-1">
                    <NumericInput
                        label="Cushion Width (m)"
                        value={width}
                        onChange={(v) => setWidth(v ?? DEFAULT_WIDTH)}
                        allowFloat
                        disabled={!isEditing}
                        type="number"
                        step="0.1"
                    />
                </div>

                <div className="w-full m-1">
                    <NumericInput
                        label="Cushion Length (m)"
                        value={length}
                        onChange={(v) => setLength(v ?? DEFAULT_LENGTH)}
                        allowFloat
                        disabled={!isEditing}
                        type="number"
                        step="0.1"
                    />
                </div>

                {outOfRange && (
                    <p className="text-sm text-red-600 w-full">
                        This cushion is stored as {width.toFixed(2)} m by {length.toFixed(2)} m, outside the
                        {" "}{MIN_DIM}–{MAX_WIDTH} m width and {MIN_DIM}–{MAX_LENGTH} m length this layout supports.
                        Correct it before saving.
                    </p>
                )}

                <AuthorizeView claim={CustomClaimValue.EditSiteLayout} facilityId={facility.id} organizationId={facility.organizationId}>
                    <div className="flex gap-2 mt-2">
                        {!isEditing ? (
                            <Button className="btn btn-outline-brand-orange mx-0 flex items-center gap-1 whitespace-nowrap" onClick={() => setIsEditing(true)}>
                                <Icons.Edit size={16} />
                                Edit Layout
                            </Button>
                        ) : (
                            <>
                                <Button onClick={handleSave} disabled={isSaving || outOfRange}>
                                    {isSaving ? "Saving..." : "Save"}
                                </Button>
                                <Button onClick={handleCancel} disabled={isSaving}>
                                    Cancel
                                </Button>
                            </>
                        )}
                    </div>
                </AuthorizeView>
            </div>
            </div>
        </div>
    );
};
